import { useMemo, useState } from "react";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { DateTimeField } from "@/components/ui/datetime-field";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import type { MachineDetail } from "@/features/lines/hooks";
import type { ParameterDetail } from "@/features/line-editor/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { formatRelativeTime, getMachineStatusFriendlyLabel } from "@/lib/display-labels";
import { useRegisterStatusEntry } from "./hooks";

interface StatusEntryBlockProps {
  machine: MachineDetail;
  lineId: string;
  statusParam: ParameterDetail;
}

const STATUS_LABEL: Record<string, string> = {
  RUNNING: "Rodando",
  STOPPED: "Parada",
  IDLE: "Ociosa",
  NODATA: "Sem dados",
};

export function StatusEntryBlock({ machine, lineId, statusParam }: StatusEntryBlockProps) {
  const mapping = (statusParam.statusMapping ?? {}) as Record<string, string>;
  const options = Object.keys(mapping);
  const [value, setValue] = useState<string>(options[0] ?? "");
  const [occurredAt, setOccurredAt] = useState<string | null>(() => new Date().toISOString());
  const register = useRegisterStatusEntry(machine.id, lineId);

  const { nowIso, minIso } = useMemo(() => {
    const now = new Date();
    return {
      nowIso: now.toISOString(),
      minIso: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
    };
  }, []);
  const selectedTargetStatus = mapping[value];
  const isSameAsCurrent = selectedTargetStatus === machine.status;

  async function handleSubmit() {
    if (!value) return;
    const occurred = occurredAt ?? new Date().toISOString();
    try {
      const result = await register.mutateAsync({ value, occurredAt: occurred });
      if (result.noop) {
        toast.info("Status já era esse — nenhuma mudança registrada.");
      } else {
        toast.success(
          `Status atualizado para ${STATUS_LABEL[result.machineStatus] ?? result.machineStatus}.`
        );
      }
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao registrar status.", { description: msg });
    }
  }

  return (
    <section className="space-y-2 rounded-md border p-3">
      <header className="space-y-1">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
          Atualizar status da máquina
        </h3>
        <div className="flex items-center gap-2 text-xs">
          <StatusBadge status={machine.status} kind="machine" />
          <span className="text-muted-foreground">
            {machine.statusSince
              ? `${getMachineStatusFriendlyLabel(machine.status)} desde ${formatRelativeTime(machine.statusSince)}`
              : "sem registro"}
          </span>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="se-value" className="text-[11px]">
            Novo status
          </Label>
          <select
            id="se-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            {options.map((k) => (
              <option key={k} value={k}>
                {k} → {STATUS_LABEL[mapping[k] ?? ""] ?? mapping[k]}
              </option>
            ))}
          </select>
        </div>
        <DateTimeField
          id="se-when"
          label="Quando aconteceu?"
          value={occurredAt}
          onChange={setOccurredAt}
          minIso={minIso}
          maxIso={nowIso}
        />
      </div>

      <Button
        size="sm"
        className="w-full"
        onClick={() => void handleSubmit()}
        disabled={register.isPending || isSameAsCurrent || !value}
      >
        {register.isPending
          ? "Registrando…"
          : isSameAsCurrent
            ? "Status atual já é esse"
            : "Registrar"}
      </Button>
    </section>
  );
}
